import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { cn } from '../utils/cn';
import { settingsApi, branchOfficesApi, messageApi } from '../services/api';

interface BranchOffice {
  id: number;
  city_zh: string;
  city_vi: string;
  address_zh: string;
  address_vi: string;
  phone: string;
  email: string;
  sort_order: number;
  is_active: number;
}

const ContactPage: React.FC = () => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [siteSettings, setSiteSettings] = useState<Record<string, string>>({});
  const [branchOffices, setBranchOffices] = useState<BranchOffice[]>([]);

  useEffect(() => {
    // 并行获取网站设置和分公司数据
    Promise.all([
      settingsApi.getWebsiteSettings().catch(() => ({ success: false, data: {} })),
      branchOfficesApi.getAll(true).catch(() => ({ success: false, data: [] }))
    ]).then(([settingsRes, branchesRes]) => {
      if (settingsRes.success && settingsRes.data) {
        setSiteSettings(settingsRes.data);
      }
      if (branchesRes.success && branchesRes.data?.length > 0) {
        setBranchOffices(branchesRes.data);
      }
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await messageApi.createMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
      });

      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });

      // 3秒后重置状态
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    } catch (err) {
      console.error('提交咨询信息失败:', err);
      setIsSubmitting(false);
      setSubmitStatus('error');

      // 3秒后重置状态
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    }
  };

  // 联系信息（从网站设置读取）
  const contactInfo = [
    {
      icon: '📍',
      title: {
        zh: '公司总部',
        vi: 'Trụ sở chính'
      },
      content: {
        zh: siteSettings.company_address_zh || '越南北宁省',
        vi: siteSettings.company_address_vi || 'Bắc Ninh, Việt Nam'
      },
      description: {
        zh: '位于北宁省核心工业园区，交通便利，配套设施完善',
        vi: 'Nằm trong khu công nghiệp trọng điểm tỉnh Bắc Ninh, giao thông thuận tiện, cơ sở hạ tầng hoàn thiện'
      }
    },
    {
      icon: '📞',
      title: {
        zh: '客户服务热线',
        vi: 'Đường dây nóng dịch vụ khách hàng'
      },
      content: {
        zh: siteSettings.contact_phone || '+84 987 654 321',
        vi: siteSettings.contact_phone || '+84 987 654 321'
      },
      description: {
        zh: siteSettings.working_hours_zh || '周一至周六 8:00-17:30，提供中越双语服务',
        vi: siteSettings.working_hours_vi || 'Thứ 2-6: 8:00-17:30, dịch vụ hỗ trợ song ngữ Trung-Việt'
      }
    },
    {
      icon: '✉️',
      title: {
        zh: '业务咨询邮箱',
        vi: 'Email tư vấn kinh doanh'
      },
      content: {
        zh: siteSettings.contact_email || 'info@huiya.com.vn',
        vi: siteSettings.contact_email || 'info@huiya.com.vn'
      },
      description: {
        zh: '项目咨询、报价、技术支持等相关业务',
        vi: 'Tư vấn dự án, báo giá, hỗ trợ kỹ thuật và các dịch vụ liên quan'
      }
    },
    {
      icon: '📧',
      title: {
        zh: '售后服务邮箱',
        vi: 'Email dịch vụ sau bán hàng'
      },
      content: {
        zh: siteSettings.contact_email || 'info@huiya.com.vn',
        vi: siteSettings.contact_email || 'info@huiya.com.vn'
      },
      description: {
        zh: '项目维护、技术支持、投诉建议等相关事宜',
        vi: 'Bảo trì dự án, hỗ trợ kỹ thuật, khiếu nại kiến nghị và các vấn đề liên quan'
      }
    }
  ];

  // 默认分公司数据（后备）
  const defaultBranches = [
    {
      city_zh: '胡志明市',
      city_vi: 'Thành phố Hồ Chí Minh',
      address_zh: '胡志明市 第一郡 商业中心大厦 15楼',
      address_vi: 'Tầng 15, Tòa nhà Trung tâm Thương mại, Quận 1, TP.HCM',
      phone: '+84 28 1234 5678',
      email: 'hcm@huya-construction.vn'
    },
    {
      city_zh: '河内市',
      city_vi: 'Hà Nội',
      address_zh: '河内市 巴亭郡 工业区大道 45号',
      address_vi: 'Số 45, Đại lộ Khu công nghiệp, Quận Ba Đình, Hà Nội',
      phone: '+84 24 8765 4321',
      email: 'hanoi@huya-construction.vn'
    },
    {
      city_zh: '岘港市',
      city_vi: 'Đà Nẵng',
      address_zh: '岘港市 海州郡 工业园区南区 8号',
      address_vi: 'Số 8, Khu Nam Khu công nghiệp, Quận Hải Châu, Đà Nẵng',
      phone: '+84 236 123 4567',
      email: 'danang@huya-construction.vn'
    }
  ];

  // 将扁平字段 (city_zh/city_vi) 转为嵌套格式 (city.zh/city.vi)
  const normalizeBranches = (branches: any[]) =>
    branches.map((b: any) => ({
      city: { zh: b.city_zh || b.city?.zh || '', vi: b.city_vi || b.city?.vi || '' },
      address: { zh: b.address_zh || b.address?.zh || '', vi: b.address_vi || b.address?.vi || '' },
      phone: b.phone || '',
      email: b.email || ''
    }));

  // 统一数据源
  const displayBranches = normalizeBranches(
    branchOffices.length > 0 ? branchOffices : defaultBranches
  );

  // 服务分类
  const serviceCategories = [
    { value: 'electrical', label: { zh: '电气工程咨询', vi: 'Tư vấn công trình điện' } },
    { value: 'hvac', label: { zh: '暖通空调咨询', vi: 'Tư vấn hệ thống HVAC' } },
    { value: 'ventilation', label: { zh: '通风系统咨询', vi: 'Tư vấn hệ thống thông gió' } },
    { value: 'decoration', label: { zh: '建筑装饰咨询', vi: 'Tư vấn trang trí công trình' } },
    { value: 'renovation', label: { zh: '改造工程咨询', vi: 'Tư vấn công trình cải tạo' } },
    { value: 'waterproofing', label: { zh: '防水地坪咨询', vi: 'Tư vấn sàn chống thấm' } },
    { value: 'other', label: { zh: '其他服务咨询', vi: 'Tư vấn dịch vụ khác' } }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面标题 */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {language === 'zh' ? '联系我们' : 'Liên hệ chúng tôi'}
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mb-8">
            {language === 'zh' 
              ? '无论您身在越南何处，我们都将为您提供专业的建筑工程咨询与服务。欢迎随时与我们联系。' 
              : 'Cho dù bạn ở đâu tại Việt Nam, chúng tôi đều cung cấp dịch vụ tư vấn và thi công xây dựng chuyên nghiệp. Luôn hoan nghênh liên hệ với chúng tôi.'}
          </p>
          <div className="flex flex-wrap gap-4">
            <a 
              href="#contact-form" 
              className="px-6 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              {language === 'zh' ? '立即咨询' : 'Tư vấn ngay'}
            </a>
            <a 
              href="#contact-info" 
              className="px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
            >
              {language === 'zh' ? '查看联系信息' : 'Xem thông tin liên hệ'}
            </a>
          </div>
        </div>
      </section>

      {/* 主要联系信息 */}
      <section id="contact-info" className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {language === 'zh' ? '联系我们' : 'Thông tin liên hệ'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {language === 'zh' 
                ? '选择最方便的方式与我们取得联系，我们的专业团队将为您提供及时的服务。' 
                : 'Chọn cách thức thuận tiện nhất để liên hệ với chúng tôi, đội ngũ chuyên nghiệp của chúng tôi sẽ cung cấp dịch vụ kịp thời cho bạn.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-3xl mb-4">{info.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {info.title[language]}
                </h3>
                <p className="text-lg font-medium text-blue-600 mb-3">
                  {info.content[language]}
                </p>
                <p className="text-gray-600 text-sm">
                  {info.description[language]}
                </p>
              </div>
            ))}
          </div>

          {/* 地图占位 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-blue-100 to-gray-100 rounded-2xl p-8 text-center">
              <div className="text-5xl mb-4 opacity-20">🗺️</div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                {language === 'zh' ? '公司位置地图' : 'Bản đồ vị trí công ty'}
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                {language === 'zh' 
                  ? '惠亚建筑工程有限公司总部位于越南北宁省核心工业园区，交通便利，配套设施完善。' 
                  : 'Trụ sở chính của Công ty Xây dựng Công trình Huệ Á nằm trong khu công nghiệp trọng điểm tỉnh Bắc Ninh, Việt Nam, giao thông thuận tiện, cơ sở hạ tầng hoàn thiện.'}
              </p>
              <div className="h-64 bg-gradient-to-br from-blue-200 to-gray-200 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl mb-2">📍</div>
                  <p className="font-medium text-gray-700">
                    {language === 'zh' ? '越南 北宁省 工业园区' : 'Khu công nghiệp, Bắc Ninh, Việt Nam'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 分公司信息 */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {language === 'zh' ? '各地分公司' : 'Chi nhánh trên toàn quốc'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {language === 'zh' 
                ? '我们在越南主要城市设有分公司，为客户提供本地化的专业服务。' 
                : 'Chúng tôi có chi nhánh tại các thành phố chính của Việt Nam, cung cấp dịch vụ chuyên nghiệp được địa phương hóa cho khách hàng.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {displayBranches.map((branch: any, index: number) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {branch.city[language]}
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">
                      {language === 'zh' ? '地址' : 'Địa chỉ'}
                    </h4>
                    <p className="text-gray-700">{branch.address[language]}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">
                      {language === 'zh' ? '电话' : 'Điện thoại'}
                    </h4>
                    <p className="text-gray-700">{branch.phone}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">
                      {language === 'zh' ? '邮箱' : 'Email'}
                    </h4>
                    <p className="text-gray-700">{branch.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 联系表单 */}
      <section id="contact-form" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                {language === 'zh' ? '发送咨询信息' : 'Gửi thông tin tư vấn'}
              </h2>
              <p className="text-gray-600">
                {language === 'zh' 
                  ? '请填写以下表格，我们将尽快与您联系并提供专业的解决方案。' 
                  : 'Vui lòng điền vào biểu mẫu dưới đây, chúng tôi sẽ liên hệ với bạn trong thời gian sớm nhất và cung cấp giải pháp chuyên nghiệp.'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* 基本信息 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('name')} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder={language === 'zh' ? '请输入您的姓名' : 'Vui lòng nhập tên của bạn'}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="example@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="+84 123 456 789"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'zh' ? '公司名称' : 'Tên công ty'}
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder={language === 'zh' ? '请输入公司名称' : 'Vui lòng nhập tên công ty'}
                      />
                    </div>
                  </div>

                  {/* 咨询分类 */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'zh' ? '咨询分类' : 'Loại hình tư vấn'} *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                    >
                      <option value="">{language === 'zh' ? '请选择咨询分类' : 'Vui lòng chọn loại hình tư vấn'}</option>
                      {serviceCategories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label[language]}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 详细咨询 */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      placeholder={language === 'zh' 
                        ? '请详细描述您的项目需求、预算、时间要求等信息...' 
                        : 'Vui lòng mô tả chi tiết nhu cầu dự án, ngân sách, yêu cầu thời gian, v.v...'}
                    />
                  </div>

                  {/* 提交状态 */}
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                      {language === 'zh'
                        ? '咨询信息已提交成功！我们的专业顾问将在24小时内与您联系。'
                        : 'Thông tin tư vấn đã được gửi thành công! Chuyên viên tư vấn của chúng tôi sẽ liên hệ với bạn trong vòng 24 giờ.'
                      }
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      {language === 'zh'
                        ? '提交失败，请稍后重试。'
                        : 'Gửi thất bại, vui lòng thử lại sau.'
                      }
                    </div>
                  )}

                  {/* 提交按钮 */}
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={cn(
                        "w-full py-4 px-6 rounded-lg font-semibold text-white transition-all",
                        isSubmitting
                          ? "bg-blue-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 active:transform active:scale-95"
                      )}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {language === 'zh' ? '提交中...' : 'Đang gửi...'}
                        </span>
                      ) : (
                        language === 'zh' ? '提交咨询信息' : 'Gửi thông tin tư vấn'
                      )}
                    </button>
                  </div>

                  {/* 隐私声明 */}
                  <div className="text-center text-gray-500 text-sm">
                    <p>
                      {language === 'zh'
                        ? '我们承诺保护您的隐私，您的个人信息仅用于为您提供服务，不会泄露给第三方。'
                        : 'Chúng tôi cam kết bảo vệ quyền riêng tư của bạn, thông tin cá nhân của bạn chỉ được sử dụng để cung cấp dịch vụ cho bạn, không được tiết lộ cho bên thứ ba.'}
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ区域 */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {language === 'zh' ? '常见问题' : 'Câu hỏi thường gặp'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {language === 'zh' 
                ? '关于联系我们和项目咨询的常见问题解答' 
                : 'Câu trả lời cho các câu hỏi thường gặp về liên hệ với chúng tôi và tư vấn dự án'}
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-xl p-6 shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {language === 'zh' ? '一般咨询会在多长时间内得到回复？' : 'Thông thường mất bao lâu để nhận được phản hồi tư vấn?'}
              </h3>
              <p className="text-gray-600">
                {language === 'zh'
                  ? '我们承诺在收到咨询信息后的24小时内给予初步回复。对于复杂的项目咨询，我们将在48小时内安排专业工程师与您详细沟通。'
                  : 'Chúng tôi cam kết phản hồi sơ bộ trong vòng 24 giờ sau khi nhận được thông tin tư vấn. Đối với tư vấn dự án phức tạp, chúng tôi sẽ sắp xếp kỹ sư chuyên môn liên hệ chi tiết với bạn trong vòng 48 giờ.'}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {language === 'zh' ? '是否提供现场考察服务？' : 'Có cung cấp dịch vụ khảo sát hiện trường không?'}
              </h3>
              <p className="text-gray-600">
                {language === 'zh'
                  ? '是的，对于重要的工程项目，我们可以安排工程师进行现场考察。请提前通过电话或邮件预约，并提供项目的基本信息和位置。'
                  : 'Có, đối với các dự án công trình quan trọng, chúng tôi có thể sắp xếp kỹ sư khảo sát hiện trường. Vui lòng đặt lịch trước qua điện thoại hoặc email và cung cấp thông tin cơ bản và vị trí của dự án.'}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {language === 'zh' ? '是否可以提供中越双语服务？' : 'Có thể cung cấp dịch vụ song ngữ Trung-Việt không?'}
              </h3>
              <p className="text-gray-600">
                {language === 'zh'
                  ? '是的，我们的团队中有精通中文和越南语的专业人员，可以为中资企业提供全程双语服务，确保沟通无障碍。'
                  : 'Có, trong đội ngũ của chúng tôi có các chuyên gia thành thạo cả tiếng Trung và tiếng Việt, có thể cung cấp dịch vụ song ngữ toàn trình cho doanh nghiệp Trung Quốc, đảm bảo giao tiếp không gặp trở ngại.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;